<template>
  <div class="motor-valuer-picker">
    <p v-if="hint" class="step-hint tw-mb-3">{{ hint }}</p>
    <label class="input-label">Your area / town <span class="req">*</span></label>
    <v-select
      :value="locationFilter"
      :items="townOptions"
      outlined
      dense
      hide-details="auto"
      placeholder="Select town"
      class="custom-input tw-mb-3"
      @change="onLocationChange"
    />
    <div v-if="locationFilter === 'Nairobi'" class="nairobi-chips tw-mb-3">
      <button
        v-for="area in nairobiAreas"
        :key="area"
        type="button"
        :class="['nairobi-area-chip', searchQuery === area ? 'area-active' : '']"
        @click="toggleArea(area)"
      >
        {{ area }}
      </button>
    </div>
    <div v-if="locationFilter">
      <p class="valuer-list-label">
        Available valuers
        <span class="valuer-count">{{ filtered.length }} found in {{ searchQuery || locationFilter }}</span>
      </p>
      <div v-if="filtered.length === 0" class="valuer-empty-state">
        <v-icon color="#94a3b8">mdi-map-marker-off-outline</v-icon>
        <p>No valuers found for this area.</p>
      </div>
      <div v-else class="valuer-cards-list">
        <button
          v-for="valuer in filtered"
          :key="valuer.id"
          type="button"
          :class="['valuer-card', selectedName === valuer.name ? 'valuer-card-selected' : '']"
          @click="selectValuer(valuer)"
        >
          <div class="vc-top">
            <div class="vc-avatar">{{ initials(valuer.name) }}</div>
            <div class="vc-info">
              <p class="vc-name">{{ valuer.name }}</p>
              <p class="vc-hq">{{ valuer.hq }}</p>
            </div>
            <v-icon v-if="selectedName === valuer.name" small color="#00AEEF">mdi-check-circle</v-icon>
          </div>
          <div class="vc-meta">
            <span><v-icon x-small color="#64748b" class="tw-mr-1">mdi-account-outline</v-icon>{{ valuer.contact }}</span>
            <span><v-icon x-small color="#64748b" class="tw-mr-1">mdi-phone-outline</v-icon>{{ valuer.phone }}</span>
          </div>
          <div v-if="branch(valuer)" class="vc-branch">
            <v-icon x-small color="#64748b" class="tw-mr-1">mdi-map-marker</v-icon>
            {{ branch(valuer).location }}: {{ branch(valuer).address }}
          </div>
        </button>
      </div>
    </div>
    <div v-if="selectedName" class="valuation-date-row tw-mt-4">
      <label class="input-label">Valuation date <span class="req">*</span></label>
      <v-text-field
        :value="valuationDate"
        :min="coverStartDate"
        :max="maxValuation"
        type="date"
        outlined
        dense
        hide-details="auto"
        class="custom-input"
        @input="$emit('update:valuationDate', $event)"
      />
      <div class="valuer-notify-note">
        <v-icon x-small color="#059669" class="tw-mr-1">mdi-email-check-outline</v-icon>
        The valuer, client and underwriting team will be notified automatically upon successful payment.
      </div>
    </div>
  </div>
</template>

<script>
import {
  MOTOR_VALUERS,
  MOTOR_NAIROBI_AREAS,
  getValuerTownOptions,
  filterValuers,
  relevantValuerBranch,
} from '@/constants/motorValuers';
import { maxValuationDate } from '@/utils/motorCoverDates';

export default {
  name: 'MotorValuerPicker',
  props: {
    coverStartDate: { type: String, default: '' },
    locationFilter: { type: String, default: '' },
    searchQuery: { type: String, default: '' },
    selectedName: { type: String, default: '' },
    valuationDate: { type: String, default: '' },
    hint: {
      type: String,
      default: 'Select a valuer near you and schedule the valuation (within 14 days of cover start).',
    },
  },
  data() {
    return { valuers: MOTOR_VALUERS };
  },
  computed: {
    townOptions() {
      return getValuerTownOptions(this.valuers);
    },
    nairobiAreas() {
      return MOTOR_NAIROBI_AREAS;
    },
    filtered() {
      return filterValuers(this.valuers, this.locationFilter, this.searchQuery);
    },
    maxValuation() {
      return maxValuationDate(this.coverStartDate);
    },
  },
  methods: {
    initials(name) {
      return String(name || '').split(' ').slice(0, 2).map((w) => w[0])
        .join('');
    },
    branch(valuer) {
      return relevantValuerBranch(valuer, this.locationFilter, this.searchQuery);
    },
    onLocationChange(val) {
      this.$emit('update:locationFilter', val);
      this.$emit('update:searchQuery', '');
      this.$emit('select', null);
    },
    toggleArea(area) {
      const next = this.searchQuery === area ? '' : area;
      this.$emit('update:searchQuery', next);
    },
    selectValuer(valuer) {
      this.$emit('select', valuer);
    },
  },
};
</script>

<style scoped lang="scss">
@import '@/styles/motor-sale-shared.scss';

/* Self-contained so the picker renders the same outside the motor sale pages (e.g. renewals). */
.motor-valuer-picker {
  .step-hint {
    font-size: 0.82rem;
    color: #64748b;
    line-height: 1.5;
  }

  .input-label {
    display: block;
    font-size: 0.8rem;
    font-weight: 600;
    color: #334155;
    margin-bottom: 6px;
  }

  .req {
    color: #dc2626;
  }

  ::v-deep .custom-input.v-text-field > .v-input__control > .v-input__slot {
    background: #ffffff !important;
    border-radius: 8px !important;
    border: 1px solid #e2e8f0 !important;
    min-height: 44px !important;
    box-shadow: none !important;
  }

  ::v-deep .custom-input.v-text-field fieldset {
    border: none !important;
  }

  ::v-deep .custom-input.v-text-field.v-input--is-focused > .v-input__control > .v-input__slot {
    border-color: #00aeef !important;
    box-shadow: 0 0 0 3px rgba(0, 174, 239, 0.15) !important;
  }

  ::v-deep .custom-input input,
  ::v-deep .custom-input .v-select__selection {
    font-size: 0.9rem !important;
    font-weight: 500 !important;
    color: #1e4276 !important;
  }
}

.valuation-date-row .custom-input {
  max-width: 280px;
}
</style>
